import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Phone, ShieldCheck } from "lucide-react";
import api, { trackEvent } from "../lib/api";
import SEO from "../components/SEO";

const TIMELINES = [
  { id: "asap", label: "As soon as possible" },
  { id: "1-3m", label: "Within 1–3 months" },
  { id: "3-6m", label: "3–6 months out" },
  { id: "planning", label: "Just planning / researching" },
];

export default function Quote() {
  const [params] = useSearchParams();
  const estimateId = params.get("estimate_id");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    timeline: "1-3m",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name || !form.email || !form.zip_code) {
      setError("Please fill in your name, email, and ZIP.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/quote", { ...form, estimate_id: estimateId });
      trackEvent("quote_submitted", { ...form, estimate_id: estimateId });
      setDone(true);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="container-x py-24 max-w-xl text-center" data-testid="quote-success">
        <CheckCircle2 size={42} className="text-brand mx-auto mb-5" />
        <h1 className="font-display text-4xl font-semibold tracking-tight">
          Request received.
        </h1>
        <p className="mt-4 text-ink-muted">
          We'll match you with vetted contractors in your area within one business day. In the meantime, bring your project brief to any quote conversation.
        </p>
        <Link to="/" className="btn-primary mt-8 inline-flex">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="container-x py-16 max-w-3xl">
      <SEO
        title="Get Local Contractor Quotes"
        description="Get matched with vetted local contractors for your bathroom remodel."
        path="/quote"
        noIndex
      />
      <p className="overline mb-4">Get local quotes</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
        Tell us about your project.
      </h1>
      <p className="mt-4 text-ink-muted max-w-xl">
        We'll match you with up to 3 vetted local contractors. No obligation, no high-pressure follow-up.
      </p>

      <form onSubmit={submit} className="surface-card mt-10 p-8 lg:p-10 space-y-5" data-testid="quote-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Your name *">
            <input
              data-testid="quote-input-name"
              className="input-field"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Field>
          <Field label="Email *">
            <input
              data-testid="quote-input-email"
              type="email"
              className="input-field"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </Field>
          <Field label="Phone (optional)">
            <input
              data-testid="quote-input-phone"
              className="input-field"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
          <Field label="ZIP code *">
            <input
              data-testid="quote-input-zip"
              inputMode="numeric"
              maxLength={5}
              className="input-field"
              value={form.zip_code}
              onChange={(e) =>
                setForm({
                  ...form,
                  zip_code: e.target.value.replace(/\D/g, "").slice(0, 5),
                })
              }
              required
            />
          </Field>
        </div>

        <Field label="Project timeline">
          <div className="grid sm:grid-cols-2 gap-2" data-testid="quote-timeline-options">
            {TIMELINES.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`quote-timeline-${t.id}`}
                onClick={() => setForm({ ...form, timeline: t.id })}
                className={`text-left p-4 rounded-md border transition ${
                  form.timeline === t.id
                    ? "border-brand bg-brand-tint"
                    : "border-line bg-surface hover:border-ink-faint"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Anything else? (optional)">
          <textarea
            data-testid="quote-input-notes"
            className="input-field min-h-[100px]"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Anything that would help a contractor understand your project."
          />
        </Field>

        {error && <p className="text-sm text-warn" data-testid="quote-error">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          data-testid="quote-submit"
          className="btn-primary w-full py-4 text-base"
        >
          <Phone size={16} />
          {submitting ? "Sending…" : "Request matched quotes"}
        </button>

        <p className="text-xs text-ink-faint flex items-center gap-2 justify-center pt-2">
          <ShieldCheck size={12} /> Your info is shared only with contractors you choose to engage.
        </p>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-ink mb-2">{label}</span>
    {children}
  </label>
);
